import { useTranslation } from 'react-i18next';
import httpClient from "../../../clients/httpClient";
import { toast } from 'react-toastify';
import React from "react";
import '../../../assets/css/checkMark.css';
import Turnstile, { useTurnstile } from "react-turnstile";

interface RequestFormProps {
    showDestinationInput: boolean,
    packageId?: string
}

export default function RequestForm(props: RequestFormProps) {
    const { t } = useTranslation(['requestForm', 'common']);

    const [requestSent, setRequestSent] = React.useState<boolean>(false);
    const [captchaResponse, setCaptchaResponse] = React.useState<string>("");
    const [passengerDropdownOpen, setPassengerDropdownOpen] = React.useState<boolean>(false);
    const [adultCount, setAdultCount] = React.useState<number | null>(1);
    const [child8To14Count, setChild8To14Count] = React.useState<number | null>(0);
    const [child2To7Count, setChild2To7Count] = React.useState<number | null>(0);
    const [childUnder2Count, setChildUnder2Count] = React.useState<number | null>(0);
    const turnstile = useTurnstile();

    const passengerLabel = `${adultCount ?? 1} ${t("adults")} ${(child8To14Count ?? 0) + (child2To7Count ?? 0) + (childUnder2Count ?? 0)} ${t("children")}`;

    const passengerDropdownRef = React.useRef<HTMLDivElement>(null);
    const checkMarkRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (!passengerDropdownOpen) {
            setAdultCount((prev) => (prev === null || prev < 1 ? 1 : prev));
            setChild8To14Count((prev) => (prev === null || prev < 0 ? 0 : prev));
            setChild2To7Count((prev) => (prev === null || prev < 0 ? 0 : prev));
            setChildUnder2Count((prev) => (prev === null || prev < 0 ? 0 : prev));
            return;
        }

        const handleDocumentClick = (event: MouseEvent) => {
            if (passengerDropdownRef.current && !passengerDropdownRef.current.contains(event.target as Node)) {
                setPassengerDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [passengerDropdownOpen]);
    React.useEffect(() => {
        if (requestSent && checkMarkRef.current) {
            checkMarkRef.current.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'center'
            });
        }
    }, [requestSent]);

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();

        const target = event.target as typeof event.target & {
            destinationInput: { value: string } | undefined;
            tripDurationInput: { value: string };
            tripDateStartInput: { value: string };
            tripDateEndInput: { value: string };
            budgetInput: { value: number };
            nameInput: { value: string };
            phoneInput: { value: string };
            emailInput: { value: string };
            packageId: { value: string } | undefined;
        };

        if (captchaResponse === "") {
            toast.error(t("captchaNotCompleted", { ns: 'common' }));
            return;
        }

        try {
            await httpClient.post(`/v1/OfferRequests`, {
                "destination": target.destinationInput ? target.destinationInput.value : 'N/A',
                "durations": target.tripDurationInput.value,
                "startDate": target.tripDateStartInput.value,
                "endDate": target.tripDateEndInput.value,
                "budget": target.budgetInput.value,
                "adultCount": adultCount ?? 1,
                "children7To14Count": child8To14Count ?? 0,
                "children2To7Count": child2To7Count ?? 0,
                "childrenUnder2Count": childUnder2Count ?? 0,
                "name": target.nameInput.value,
                "phone": target.phoneInput.value,
                "email": target.emailInput.value,
                "packageId": props.packageId,
                "captchaResponse": captchaResponse
            });

            setRequestSent(true);
        }
        catch (e: unknown) {
            const error = e as any;
            console.log(error);
            if (error?.response?.data?.message)
                toast.error(error.response.data.message);
            else
                toast.error(t("unexpectedError", { ns: 'common' }));
        }
        finally {
            turnstile.reset();
            setCaptchaResponse("");
        }
    }

    return (
        <>
            {
                requestSent ?
                    <div ref={checkMarkRef}>
                        <svg className="checkMark" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
                            <circle className="path circle" fill="none" stroke="#73AF55" strokeWidth="6" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
                            <polyline className="path check" fill="none" stroke="#73AF55" strokeWidth="6" strokeLinecap="round" strokeMiterlimit="10" points="100.2,40.2 51.5,88.8 29.8,67.5 " />
                        </svg>
                        <p className="checkMarkText success">{t("successStatusMsg")}</p>
                    </div>
                    :
                    <div className="contact-form" onSubmit={handleSubmit}>
                        <form action="#checkMark" >
                            <h5 className="contact-d-head">{t('title')}</h5>
                            <div className="row">
                                {
                                    props.showDestinationInput &&
                                    <div className="form-group">
                                        <label htmlFor="destinationInput">{t("destination")}</label>
                                        <input type="text" className="form-control" id="destinationInput" aria-describedby="destinationHelp" placeholder="" required />
                                        <small id="destinationHelp" className="form-text text-muted">{t("destinationTip")}</small>
                                    </div>
                                }

                                <div className="form-group">
                                    <label htmlFor="tripDurationInput">{t("tripDuration")}</label>
                                    <input type="text" className="form-control" id="tripDurationInput" placeholder="5, 7, 9, 10" required />
                                </div>

                                <div className="col-lg-6">
                                    <div className="form-group">
                                        <label htmlFor="tripDateStartInput">{t("tripDateStart")}</label>
                                        <input type="date" className="form-control" id="tripDateStartInput" aria-describedby="tripDateStartHelp" required />
                                        <small id="tripDateStartHelp" className="form-text text-muted">{t("tripDateStartTip")}</small>
                                    </div>
                                </div>

                                <div className="col-lg-6">
                                    <div className="form-group">
                                        <label htmlFor="tripDateEndInput">{t("tripDateEnd")}</label>
                                        <input type="date" className="form-control" id="tripDateEndInput" aria-describedby="tripDateEndHelp" required />
                                        <small id="tripDateEndHelp" className="form-text text-muted">{t("tripDateEndTip")}</small>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="budgetInput">{t("budget")}</label>
                                    <input type="number" min="1" step="0.01" className="form-control" id="budgetInput" required />
                                </div>

                                <div className="form-group passenger-dropdown" ref={passengerDropdownRef}>
                                    <label htmlFor="passengerInput">{t("passengers")}</label>
                                    <div
                                        className="passenger-dropdown-control"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setPassengerDropdownOpen((open) => !open)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setPassengerDropdownOpen((open) => !open);
                                            }
                                        }}
                                    >
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="passengerInput"
                                            value={passengerLabel}
                                            readOnly
                                            required
                                        />
                                    </div>

                                    {passengerDropdownOpen && (
                                        <div
                                            className="passenger-subinputs"
                                        >
                                            <div className="form-group">
                                                <label htmlFor="adultCountInput">{t("adults")}</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="form-control"
                                                    id="adultCountInput"
                                                    value={adultCount ?? ''}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        if (inputValue === '') {
                                                            setAdultCount(null);
                                                        } else {
                                                            const num = parseInt(inputValue, 10);
                                                            if (!isNaN(num)) {
                                                                setAdultCount(num);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="child8To14CountInput">{t("chld8To14")}</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    id="child8To14CountInput"
                                                    value={child8To14Count ?? ''}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        if (inputValue === '') {
                                                            setChild8To14Count(null);
                                                        } else {
                                                            const num = parseInt(inputValue, 10);
                                                            if (!isNaN(num)) {
                                                                setChild8To14Count(num);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="child2To7CountInput">{t("chld2To7")}</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    id="child2To7CountInput"
                                                    value={child2To7Count ?? ''}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        if (inputValue === '') {
                                                            setChild2To7Count(null);
                                                        } else {
                                                            const num = parseInt(inputValue, 10);
                                                            if (!isNaN(num)) {
                                                                setChild2To7Count(num);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label htmlFor="childUnder2CountInput">{t("chldUnder2")}</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    id="childUnder2CountInput"
                                                    value={childUnder2Count ?? ''}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        if (inputValue === '') {
                                                            setChildUnder2Count(null);
                                                        } else {
                                                            const num = parseInt(inputValue, 10);
                                                            if (!isNaN(num)) {
                                                                setChildUnder2Count(num);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <h5 className="contact-d-head">{t('contactInfo')}</h5>

                                <div className="form-group">
                                    <label htmlFor="nameInput">{t("name")}</label>
                                    <input type="text" className="form-control" id="nameInput" required />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phoneInput">{t("phone")}</label>
                                    <input type="phone" className="form-control" id="phoneInput" required />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="emailInput">{t("email")}</label>
                                    <input type="emailInput" className="form-control" id="emailInput" required />
                                </div>

                                <Turnstile
                                    sitekey={process.env.REACT_APP_TURNSTILE_KEY ?? (() => { throw new Error("Turnstile site key not provided") })()}
                                    onVerify={(token) => {
                                        setCaptchaResponse(token);
                                    }}
                                />

                                <div className="col-lg-12 mb-3">
                                    <input type="submit" value={t("send").toString()} />
                                </div>
                            </div>
                        </form>
                    </div>
            }
        </>
    );
}
